/**
 * Quest catalogue — starter quests at each faction spawn + a few cross-zone
 * quests. Phase 6 ships a static catalogue; future expansions add daily/
 * weekly + faction-specific narrative chains.
 *
 * Quest types:
 *   kill — kill N mobs of kind K
 *   gather — collect N of material id M
 *   visit — reach a coordinate (waypoint)
 *   deliver — bring item I to NPC X
 *
 * All quests give XP + a faction-appropriate item or gold reward. Big
 * "story" quests gate on level + completion of prereqs.
 */

import type { Side } from './factions.js';

export type QuestType = 'kill' | 'gather' | 'visit' | 'deliver';

export interface Quest {
  id: string;
  giver: string; // NPC id
  giverName: { ru: string; en: string };
  giverSide: Side | null;
  giverPos: { x: number; z: number };
  /** Min level to accept. */
  minLevel: number;
  /** Max level (offered greyed-out above this). */
  maxLevel: number;
  title: { ru: string; en: string };
  description: { ru: string; en: string };
  type: QuestType;
  /** What to do — interpretation depends on type. */
  objective: {
    /** kill: mob kind  · gather: material id · deliver: item id · visit: pos */
    target?: string;
    pos?: { x: number; z: number; radius?: number };
    /** Required count. */
    count?: number;
  };
  /** Rewards. */
  reward: {
    xp: number;
    gold?: number;
    itemId?: string;
    realmPoints?: number;
  };
  /** Quest IDs that must be completed first. */
  prereqs?: string[];
}

export const QUESTS: Quest[] = [
  // R115: WoW Vanilla canon квесты — Greenwald Vale (Alliance) +
  // Sharpcrest (Horde) starter chains. Имена/цели/награды близкие к
  // оригиналу, координаты под нашу карту 1500м radius.
  // ── ELWYNN — Commander Tiren Steelhorn (Coppergrove) ─────────────────────────
  {
    id: 'tidal-001',
    giver: 'old-fisherman',
    giverName: { ru: 'Командор Тирен Стальгорн', en: 'Commander Tiren Steelhorn' },
    giverSide: 'tidal',
    giverPos: { x: -1450, z: 70 },
    minLevel: 1,
    maxLevel: 5,
    title: { ru: 'Волки за границей', en: 'Wolves Across the Border' },
    description: {
      ru: 'Волки Зелёного Дола стали наглыми и нападают на путников у дороги к Меднорощи. Убей 8 молодых волков и принеси мне доказательство.',
      en: 'The wolves of Greenwald Vale have grown bold, attacking travelers on the road to Coppergrove. Kill 8 Young Wolves and bring me proof.',
    },
    type: 'kill',
    objective: { target: 'wolf', count: 8 },
    reward: { xp: 250, gold: 30, itemId: 'leather-cap' },
  },
  {
    id: 'tidal-002',
    giver: 'old-fisherman',
    giverName: { ru: 'Командор Тирен Стальгорн', en: 'Commander Tiren Steelhorn' },
    giverSide: 'tidal',
    giverPos: { x: -1450, z: 70 },
    minLevel: 3,
    maxLevel: 8,
    title: { ru: 'Гарнизон Уэстбрука нуждается в помощи', en: 'Westbrook Garrison Needs Help!' },
    description: {
      ru: 'Гноллы из стаи Речной Лапы атакуют гарнизон Уэстбрука. Иди на запад и убей 8 гноллов, чтобы помочь стражникам удержать стены.',
      en: 'Riverpaw gnolls are attacking Westbrook Garrison. Head west and kill 8 gnolls to help the guards hold the walls.',
    },
    type: 'kill',
    objective: { target: 'goblin', count: 8 },
    reward: { xp: 380, gold: 50, itemId: 'leather-jerkin' },
    prereqs: ['tidal-001'],
  },
  // ── ELWYNN — Smith Argus / William Pestle (Coppergrove) ──────────
  {
    id: 'rooted-001',
    giver: 'master-smith',
    giverName: { ru: 'Кузнец Аргус', en: 'Smith Argus' },
    giverSide: 'rooted',
    giverPos: { x: -1455, z: 95 },
    minLevel: 1,
    maxLevel: 5,
    title: { ru: 'Бандиты Тёмный Меч в карьере', en: 'Dark Sword Bandits in the Quarry' },
    description: {
      ru: 'Кобольды-вредители на руднике Эхо Ридж распугали шахтёров. Очисти шахту — убей 10 кобольдов и принеси доказательства.',
      en: 'Kobold Vermin in Echo Ridge Mine have scared off the miners. Clear the mine — kill 10 kobolds and bring proof.',
    },
    type: 'kill',
    objective: { target: 'goblin', count: 10 },
    reward: { xp: 280, gold: 40, itemId: 'iron-sword' },
  },
  {
    id: 'rooted-002',
    giver: 'master-smith',
    giverName: { ru: 'Кузнец Аргус', en: 'Smith Argus' },
    giverSide: 'rooted',
    giverPos: { x: -1455, z: 95 },
    minLevel: 4,
    maxLevel: 8,
    title: { ru: 'Доли с разбойниками', en: 'Deliveries to Stonefield Farm' },
    description: {
      ru: 'Разбойники Тёмный Меч обосновались в тыквенном поле Бракуэлла. Убей 8 разбойников, чтобы фермеры могли вернуться к работе.',
      en: 'Dark Sword Bandits have set up in Brackwell Pumpkin Patch. Kill 8 bandits so the farmers can return to work.',
    },
    type: 'kill',
    objective: { target: 'bandit', count: 8 },
    reward: { xp: 420, gold: 70, itemId: 'mail-hauberk' },
    prereqs: ['rooted-001'],
  },
  // ── DUROTAR — Foreman Thazz'ril / Orgnil Soulscar (Sharpridge) ──
  {
    id: 'anchorless-001',
    giver: 'caravan-master',
    giverName: { ru: 'Десятник Тазз\'рил', en: 'Foreman Thazz\'ril' },
    giverSide: 'anchorless',
    giverPos: { x: 1450, z: -70 },
    minLevel: 1,
    maxLevel: 5,
    title: { ru: 'Прорезание зубов', en: 'Cutting Teeth' },
    description: {
      ru: 'Молодой воин Бурного Племени должен показать клыки. Убей 6 пятнистых кабанов в Долине Испытаний и возвращайся, когда руки будут красными.',
      en: 'A young warrior of the Storm Tribe must show his teeth. Kill 6 Mottled Boars in the Valley of Trials and return when your hands are red.',
    },
    type: 'kill',
    objective: { target: 'boar', count: 6 },
    reward: { xp: 260, gold: 35, itemId: 'oak-bow' },
  },
  {
    id: 'anchorless-002',
    giver: 'caravan-master',
    giverName: { ru: 'Десятник Тазз\'рил', en: 'Foreman Thazz\'ril' },
    giverSide: 'anchorless',
    giverPos: { x: 1450, z: -70 },
    minLevel: 3,
    maxLevel: 8,
    title: { ru: 'Ленивые батраки', en: 'Lazy Peons' },
    description: {
      ru: 'Возьми Дубинку Десятника и пробуди 5 спящих батраков на лесопилке. Только Племя работает — ленивые не нужны.',
      en: 'Take the Foreman\'s Blackjack and wake 5 sleeping peons at the lumber mill. The Storm Tribe labors — lazy ones are not needed.',
    },
    type: 'visit',
    objective: { pos: { x: 700, z: 60, radius: 30 } },
    reward: { xp: 200, gold: 60 },
    prereqs: ['anchorless-001'],
  },
  // ── Tidal extension chain — 5 quests deepening Old Jürgen's story ──
  {
    id: 'tidal-003',
    giver: 'old-fisherman',
    giverName: { ru: 'Командор Тирен Стальгорн', en: 'Commander Tiren Steelhorn' },
    giverSide: 'tidal',
    giverPos: { x: -1450, z: 70 },
    minLevel: 4,
    maxLevel: 9,
    title: { ru: 'Шкура для Сети', en: 'Hide for the Net' },
    description: {
      ru: 'Старая сеть рвётся. Принеси 4 шкуры волков — обтянем порванные верёвки.',
      en: 'Old net is tearing. Bring 4 wolf pelts to patch the rope.',
    },
    type: 'gather',
    objective: { target: 'wolf-pelt', count: 4 },
    reward: { xp: 320, gold: 45, itemId: 'leather-jerkin' },
    prereqs: ['tidal-001'],
  },
  {
    id: 'tidal-004',
    giver: 'old-fisherman',
    giverName: { ru: 'Командор Тирен Стальгорн', en: 'Commander Tiren Steelhorn' },
    giverSide: 'tidal',
    giverPos: { x: -1450, z: 70 },
    minLevel: 6,
    maxLevel: 12,
    title: { ru: 'Пауки в Подземелье Дока', en: 'Spiders in the Dock Cellar' },
    description: {
      ru: 'Подвал доков заполонили пауки. Выгони их — убей 8. Берегись больших.',
      en: 'Dockside cellar overrun with spiders. Drive them out — slay 8. Watch the big ones.',
    },
    type: 'kill',
    objective: { target: 'spider', count: 8 },
    reward: { xp: 480, gold: 80, itemId: 'leather-jerkin' },
    prereqs: ['tidal-002'],
  },
  {
    id: 'tidal-005',
    giver: 'old-fisherman',
    giverName: { ru: 'Командор Тирен Стальгорн', en: 'Commander Tiren Steelhorn' },
    giverSide: 'tidal',
    giverPos: { x: -1450, z: 70 },
    minLevel: 8,
    maxLevel: 14,
    title: { ru: 'Бандиты с Лодок', en: 'Boat Bandits' },
    description: {
      ru: 'Бандиты грабят рыбаков ночью. Останови их — 10 голов на пристань.',
      en: 'Bandits raid fishermen at night. Stop them — 10 heads on the pier.',
    },
    type: 'kill',
    objective: { target: 'bandit', count: 10 },
    reward: { xp: 700, gold: 130, itemId: 'mail-hauberk' },
    prereqs: ['tidal-004'],
  },
  // ── Rooted extension chain ─────────────────────────────────────
  {
    id: 'rooted-003',
    giver: 'master-smith',
    giverName: { ru: 'Кузнец Уильям', en: 'Smith William' },
    giverSide: 'rooted',
    giverPos: { x: -1455, z: 95 },
    minLevel: 5,
    maxLevel: 10,
    title: { ru: 'Кабан с Бивнями', en: 'The Tusker' },
    description: {
      ru: 'Боров-вожак рвёт мои поля. Принеси его клык — 4 штуки.',
      en: 'A boar chief tears up my fields. Bring 4 tusks.',
    },
    type: 'gather',
    objective: { target: 'boar-tusk', count: 4 },
    reward: { xp: 380, gold: 55, itemId: 'leather-cap' },
    prereqs: ['rooted-001'],
  },
  {
    id: 'rooted-004',
    giver: 'master-smith',
    giverName: { ru: 'Кузнец Уильям', en: 'Smith William' },
    giverSide: 'rooted',
    giverPos: { x: -1455, z: 95 },
    minLevel: 8,
    maxLevel: 14,
    title: { ru: 'Гоблины Глубокой Жилы', en: 'Goblins of Deepvein' },
    description: {
      ru: 'Гоблины украли мою наковальню. Убей 12, верни ремесленную честь Дипвейна.',
      en: 'Goblins stole my anvil. Kill 12 and avenge Deepvein craftsmanship.',
    },
    type: 'kill',
    objective: { target: 'goblin', count: 12 },
    reward: { xp: 850, gold: 140, itemId: 'mail-leggings' },
    prereqs: ['rooted-002'],
  },
  {
    id: 'rooted-005',
    giver: 'master-smith',
    giverName: { ru: 'Кузнец Уильям', en: 'Smith William' },
    giverSide: 'rooted',
    giverPos: { x: -1455, z: 95 },
    minLevel: 12,
    maxLevel: 20,
    title: { ru: 'Каменное Сердце', en: 'Heart of Stone' },
    description: {
      ru: 'В горах пробудились големы. Принеси 8 каменных осколков для рунической наковальни.',
      en: 'Golems woke in the peaks. Bring 8 stone shards for the runic anvil.',
    },
    type: 'gather',
    objective: { target: 'stone-shard', count: 8 },
    reward: { xp: 1400, gold: 240, itemId: 'steel-greatsword' },
    prereqs: ['rooted-004'],
  },
  // ── Anchorless extension chain ─────────────────────────────────
  {
    id: 'anchorless-003',
    giver: 'caravan-master',
    giverName: { ru: 'Капитан Грайк', en: 'Captain Grayke' },
    giverSide: 'anchorless',
    giverPos: { x: 1450, z: -70 },
    minLevel: 4,
    maxLevel: 9,
    title: { ru: 'Шёлк Серебряных Лап', en: 'Silken Silver Legs' },
    description: {
      ru: 'Контракт на шёлк. Убей пауков, принеси 6 нитей.',
      en: 'Silk contract. Slay spiders, bring 6 strands.',
    },
    type: 'gather',
    objective: { target: 'spider-silk', count: 6 },
    reward: { xp: 360, gold: 60, itemId: 'hunter-cloak' },
    prereqs: ['anchorless-001'],
  },
  {
    id: 'anchorless-004',
    giver: 'caravan-master',
    giverName: { ru: 'Капитан Грайк', en: 'Captain Grayke' },
    giverSide: 'anchorless',
    giverPos: { x: 1450, z: -70 },
    minLevel: 7,
    maxLevel: 13,
    title: { ru: 'Дороги Бандитов', en: 'Bandit Roads' },
    description: {
      ru: 'Бандиты обложили караванный путь. Прорубись — убей 10 и принеси 5 ржавых монет в доказательство.',
      en: 'Bandits choke the caravan road. Cut through — 10 heads and 5 rusted coins as proof.',
    },
    type: 'kill',
    objective: { target: 'bandit', count: 10 },
    reward: { xp: 720, gold: 130, itemId: 'shadowstride-boots' },
    prereqs: ['anchorless-002'],
  },
  {
    id: 'anchorless-005',
    giver: 'caravan-master',
    giverName: { ru: 'Капитан Грайк', en: 'Captain Grayke' },
    giverSide: 'anchorless',
    giverPos: { x: 1450, z: -70 },
    minLevel: 12,
    maxLevel: 20,
    title: { ru: 'Призраки Болот', en: 'Ghosts of the Fen' },
    description: {
      ru: 'Караван не пройдёт через Шейдоуфен пока живы вурдалаки. Прогони 10 — и принеси 4 эктоплазмы для оберегов.',
      en: 'Caravan can\'t cross Shadowfen while wraiths walk. Banish 10 and bring 4 ectoplasms for wards.',
    },
    type: 'kill',
    objective: { target: 'wraith', count: 10 },
    reward: { xp: 1500, gold: 280, itemId: 'arcane-staff' },
    prereqs: ['anchorless-004'],
  },
  // ── R106: Lionhold / Wartus Citadel столичные квестодатели ───────────
  {
    id: 'sw-king-001',
    giver: 'king-stormwind',
    giverName: { ru: 'Король Эдгар', en: 'King Edgar Lightmane' },
    giverSide: 'tidal',
    giverPos: { x: -1100, z: 12 },
    minLevel: 5,
    maxLevel: 15,
    title: { ru: 'Послание Короля', en: 'The King\'s Message' },
    description: {
      ru: 'Король призывает героев Серебряной Зари. Доложи Командору Стальгорну в Меднорощи о готовности служить Львиную Твердыню.',
      en: 'The king summons heroes of the Silver Dawn. Report to Commander Tiren Steelhorn in Coppergrove that you stand ready to serve Lionhold.',
    },
    type: 'visit',
    objective: { pos: { x: -650, z: 0, radius: 8 } },
    reward: { xp: 800, gold: 100, itemId: 'leather-jerkin' },
  },
  {
    id: 'sw-auction-001',
    giver: 'sw-auctioneer',
    giverName: { ru: 'Аукционист Жанет', en: 'Auctioneer Jaxon' },
    giverSide: 'tidal',
    giverPos: { x: -1100, z: -10 },
    minLevel: 1,
    maxLevel: 60,
    title: { ru: 'Открытие Торгов', en: 'Welcome to the Auction House' },
    description: {
      ru: 'Принеси аукционисту 5 шкур кабана для оценки и узнай как работает Аукционный Дом.',
      en: 'Bring the auctioneer 5 boar hides for appraisal and learn how the Auction House works.',
    },
    type: 'gather',
    objective: { target: 'boar-hide', count: 5 },
    reward: { xp: 400, gold: 200 },
  },
  {
    id: 'org-thrall-001',
    giver: 'thrall',
    giverName: { ru: 'Вождь Наррад', en: 'Warchief Narrad' },
    giverSide: 'anchorless',
    giverPos: { x: 1100, z: 12 },
    minLevel: 5,
    maxLevel: 15,
    title: { ru: 'Слово Наррада', en: 'Narrad\'s Word' },
    description: {
      ru: 'Наррад призывает героев Племени. Доложи Капитану Грайку в Бритвенном Холме о готовности сражаться за Бурное Племя.',
      en: 'Narrad summons heroes of the Storm Tribe. Report to Captain Grayke at Sharpridge that you stand ready to fight for the Storm Tribe.',
    },
    type: 'visit',
    objective: { pos: { x: 700, z: 0, radius: 8 } },
    reward: { xp: 800, gold: 100, itemId: 'leather-jerkin' },
  },
  {
    id: 'org-auction-001',
    giver: 'org-auctioneer',
    giverName: { ru: 'Аукционист Грилок', en: 'Auctioneer Grilok' },
    giverSide: 'anchorless',
    giverPos: { x: 1100, z: -10 },
    minLevel: 1,
    maxLevel: 60,
    title: { ru: 'Базар Силы', en: 'The Bazaar of Strength' },
    description: {
      ru: 'Принеси аукционисту 5 клыков волка для оценки и узнай как работает базар Цитадели Бранного Колеса.',
      en: 'Bring the auctioneer 5 wolf fangs for appraisal and learn how the Wartus Citadel bazaar works.',
    },
    type: 'gather',
    objective: { target: 'wolf-fang', count: 5 },
    reward: { xp: 400, gold: 200 },
  },
  // ── Storm Oracle 3-step grand chain ─────────────────────────────
  {
    id: 'epic-001',
    giver: 'storm-oracle',
    giverName: { ru: 'Архимаг Малин', en: 'Archmage Malin' },
    giverSide: null, // neutral — given in Stormveil Crossroads
    giverPos: { x: 0, z: 0 },
    minLevel: 15,
    maxLevel: 30,
    title: { ru: 'Голос Шторма', en: 'Voice of the Storm' },
    description: {
      ru: 'Оракул слышит, как древний дракон зовёт. Найди его след — убей 12 големов, чтобы открыть дверь.',
      en: 'The Oracle hears an ancient dragon call. Find its trail — slay 12 golems to open the door.',
    },
    type: 'kill',
    objective: { target: 'golem', count: 12 },
    reward: { xp: 5000, gold: 800, itemId: 'arcane-staff', realmPoints: 200 },
  },
  {
    id: 'epic-002',
    giver: 'storm-oracle',
    giverName: { ru: 'Архимаг Малин', en: 'Archmage Malin' },
    giverSide: null,
    giverPos: { x: 0, z: 0 },
    minLevel: 22,
    maxLevel: 40,
    title: { ru: 'Сердце Бури', en: 'Heart of the Storm' },
    description: {
      ru: 'Дверь открыта, но запечатана пятью огненными кристаллами. Принеси их — у драконышей в Шейдоуфене.',
      en: 'Door is open but sealed by 5 fire crystals. Take them from the dragonkin in Shadowfen.',
    },
    type: 'gather',
    objective: { target: 'fire-crystal', count: 5 },
    reward: { xp: 8500, gold: 1500, itemId: 'stormblade', realmPoints: 350 },
    prereqs: ['epic-001'],
  },
  {
    id: 'epic-003',
    giver: 'storm-oracle',
    giverName: { ru: 'Архимаг Малин', en: 'Archmage Malin' },
    giverSide: null,
    giverPos: { x: 0, z: 0 },
    minLevel: 30,
    maxLevel: 50,
    title: { ru: 'Имя Древнего', en: 'The Ancient Name' },
    description: {
      ru: 'Властитель Шторма пробуждается. Убей его — и принеси Оракулу его глаз. Имя будет произнесено.',
      en: 'The Storm Lord stirs. Slay him — bring the eye to the Oracle. The name will be spoken.',
    },
    type: 'kill',
    objective: { target: 'world-boss', count: 1 },
    reward: { xp: 25_000, gold: 5000, itemId: 'aiwari-relic-blade', realmPoints: 1000 },
    prereqs: ['epic-002'],
  },
  // ── R232q5e: Phase 2 expansion — 10 новых стартовых квестов
  // (5 для Alliance, 5 для Horde) с разными типами и целями.
  {
    id: 'tidal-starter-fox', giver: 'old-fisherman',
    giverName: { ru: 'Командор Тирен Стальгорн', en: 'Commander Tiren Steelhorn' },
    giverSide: 'tidal', giverPos: { x: -1450, z: 70 },
    minLevel: 1, maxLevel: 5,
    title: { ru: 'Лисы крадут кур', en: 'Foxes Steal Chickens' },
    description: { ru: 'Лисы повадились в курятники Меднорощи. Убей 6 лис.', en: 'Foxes raid the henhouses of Coppergrove. Kill 6 foxes.' },
    type: 'kill', objective: { target: 'fox', count: 6 },
    reward: { xp: 200, gold: 25, itemId: 'rusted-sword' },
  },
  {
    id: 'tidal-starter-bunnies', giver: 'old-fisherman',
    giverName: { ru: 'Командор Тирен Стальгорн', en: 'Commander Tiren Steelhorn' },
    giverSide: 'tidal', giverPos: { x: -1450, z: 70 },
    minLevel: 1, maxLevel: 4,
    title: { ru: 'Кроличий суп', en: "Rabbit Stew" },
    description: { ru: 'Тётушка Грета хочет приготовить суп. Принеси 5 кроличьих лапок.', en: 'Auntie Greta wants stew. Bring 5 rabbit feet.' },
    type: 'gather', objective: { target: 'wolf-fang', count: 5 },
    reward: { xp: 180, gold: 20, itemId: 'tattered-shirt' },
  },
  {
    id: 'tidal-starter-deliver', giver: 'old-fisherman',
    giverName: { ru: 'Командор Тирен Стальгорн', en: 'Commander Tiren Steelhorn' },
    giverSide: 'tidal', giverPos: { x: -1450, z: 70 },
    minLevel: 2, maxLevel: 6,
    title: { ru: 'Письмо в Львиную Твердыню', en: 'Letter to Lionhold' },
    description: { ru: 'Доставь это письмо стражнику Львиной Твердыни. Дорога идёт прямо на запад.', en: 'Deliver this letter to a Lionhold guard. The road runs west.' },
    type: 'visit', objective: { pos: { x: -3000, z: 0, radius: 50 } },
    reward: { xp: 320, gold: 35 },
  },
  {
    id: 'tidal-starter-bandits', giver: 'old-fisherman',
    giverName: { ru: 'Командор Тирен Стальгорн', en: 'Commander Tiren Steelhorn' },
    giverSide: 'tidal', giverPos: { x: -1450, z: 70 },
    minLevel: 4, maxLevel: 9,
    title: { ru: 'Разбойники у дороги', en: 'Roadside Bandits' },
    description: { ru: 'У старой развилки засели разбойники. Убей 5 разбойников.', en: 'Bandits camp at the old fork. Kill 5 bandits.' },
    type: 'kill', objective: { target: 'bandit', count: 5 },
    reward: { xp: 450, gold: 60, itemId: 'iron-sword' },
  },
  {
    id: 'tidal-starter-herbs', giver: 'old-fisherman',
    giverName: { ru: 'Командор Тирен Стальгорн', en: 'Commander Tiren Steelhorn' },
    giverSide: 'tidal', giverPos: { x: -1450, z: 70 },
    minLevel: 1, maxLevel: 5,
    title: { ru: 'Травы для лекаря', en: 'Herbs for the Healer' },
    description: { ru: 'Лекарь просит принести 8 пучков диких трав. Они растут на лугах.', en: 'Healer asks for 8 bundles of wild herbs from the meadows.' },
    type: 'gather', objective: { target: 'spider-silk', count: 8 },
    reward: { xp: 220, gold: 22, itemId: 'beggar-shoes' },
  },
  {
    id: 'anchorless-starter-bull', giver: 'tribal-chief',
    giverName: { ru: 'Вождь Громовое Сердце', en: 'Chief Thunderheart' },
    giverSide: 'anchorless', giverPos: { x: 1450, z: -70 },
    minLevel: 1, maxLevel: 5,
    title: { ru: 'Дикие быки', en: 'Wild Bulls' },
    description: { ru: 'Дикие быки топчут наши шатры. Убей 6 быков.', en: 'Wild bulls trample our tents. Kill 6 bulls.' },
    type: 'kill', objective: { target: 'bull', count: 6 },
    reward: { xp: 250, gold: 30, itemId: 'tattered-shirt' },
  },
  {
    id: 'anchorless-starter-spiderlings', giver: 'tribal-chief',
    giverName: { ru: 'Вождь Громовое Сердце', en: 'Chief Thunderheart' },
    giverSide: 'anchorless', giverPos: { x: 1450, z: -70 },
    minLevel: 1, maxLevel: 4,
    title: { ru: 'Паучьи яйца', en: 'Spider Eggs' },
    description: { ru: 'Шаман просит 5 паучьих желёз для зелья. Найди их у пауков пустыни.', en: 'Shaman wants 5 spider venom glands for a brew.' },
    type: 'gather', objective: { target: 'venom-gland', count: 5 },
    reward: { xp: 200, gold: 25, itemId: 'rusted-sword' },
  },
  {
    id: 'anchorless-starter-deliver', giver: 'tribal-chief',
    giverName: { ru: 'Вождь Громовое Сердце', en: 'Chief Thunderheart' },
    giverSide: 'anchorless', giverPos: { x: 1450, z: -70 },
    minLevel: 2, maxLevel: 6,
    title: { ru: 'Гонец в Цитадель', en: 'Runner to the Citadel' },
    description: { ru: 'Доставь свиток в Цитадель Бранного Колеса.', en: 'Deliver this scroll to Wartus Citadel.' },
    type: 'visit', objective: { pos: { x: 3000, z: 0, radius: 50 } },
    reward: { xp: 320, gold: 35 },
  },
  {
    id: 'anchorless-starter-bandits', giver: 'tribal-chief',
    giverName: { ru: 'Вождь Громовое Сердце', en: 'Chief Thunderheart' },
    giverSide: 'anchorless', giverPos: { x: 1450, z: -70 },
    minLevel: 4, maxLevel: 9,
    title: { ru: 'Изменники Племени', en: 'Traitors of the Storm Tribe' },
    description: { ru: 'Группа дикарей-изменников засела у южных скал. Убей 5 дикарей.', en: 'Traitor tribesmen lurk in southern cliffs. Kill 5 of them.' },
    type: 'kill', objective: { target: 'tribal', count: 5 },
    reward: { xp: 450, gold: 60, itemId: 'iron-sword' },
  },
  {
    id: 'anchorless-starter-cactus', giver: 'tribal-chief',
    giverName: { ru: 'Вождь Громовое Сердце', en: 'Chief Thunderheart' },
    giverSide: 'anchorless', giverPos: { x: 1450, z: -70 },
    minLevel: 1, maxLevel: 5,
    title: { ru: 'Сок кактуса', en: 'Cactus Sap' },
    description: { ru: 'Принеси шаману 7 пучков сока кактуса для напитка предков.', en: 'Bring shaman 7 bundles of cactus sap for the ancestor brew.' },
    type: 'gather', objective: { target: 'spider-silk', count: 7 },
    reward: { xp: 220, gold: 22, itemId: 'beggar-shoes' },
  },
  // ── R232q5m: Зональные квесты (один на каждую новую зону) — 27 квестов ──
  // West side
  { id: 'z-westmarch', giver: 'westmarch-marshal', giverName: { ru: 'Маршал Западного Простора', en: 'Westmarch Marshal' }, giverSide: 'tidal', giverPos: { x: -1950, z: -300 }, minLevel: 10, maxLevel: 20, title: { ru: 'Бандиты Западного Простора', en: 'Westmarch Bandits' }, description: { ru: 'Убей 8 разбойников у заброшенных полей.', en: 'Kill 8 bandits.' }, type: 'kill', objective: { target: 'bandit', count: 8 }, reward: { xp: 800, gold: 80, itemId: 'iron-sword' } },
  { id: 'z-redrock', giver: 'redrock-overseer', giverName: { ru: 'Надзиратель Краснокаменья', en: 'Redrock Overseer' }, giverSide: 'tidal', giverPos: { x: -1300, z: -1250 }, minLevel: 15, maxLevel: 25, title: { ru: 'Гоблинская яма', en: 'Goblin Pit' }, description: { ru: 'Очисти яму от 6 гоблинов.', en: 'Clear pit of 6 goblins.' }, type: 'kill', objective: { target: 'goblin', count: 6 }, reward: { xp: 1100, gold: 100, itemId: 'mail-hauberk' } },
  { id: 'z-duskhollow', giver: 'duskhollow-priest', giverName: { ru: 'Жрица Сумрака', en: 'Dusk Priestess' }, giverSide: 'tidal', giverPos: { x: -2150, z: -1100 }, minLevel: 18, maxLevel: 30, title: { ru: 'Призраки Лощины', en: 'Hollow Wraiths' }, description: { ru: 'Изгнани 7 призраков из тёмных хижин.', en: 'Banish 7 wraiths.' }, type: 'kill', objective: { target: 'wraith', count: 7 }, reward: { xp: 1400, gold: 130, itemId: 'priest-robe' } },
  { id: 'z-thorndeep', giver: 'thorndeep-explorer', giverName: { ru: 'Исследователь Тернии', en: 'Thorn Explorer' }, giverSide: null, giverPos: { x: -2450, z: 1100 }, minLevel: 30, maxLevel: 45, title: { ru: 'Тролли Гурубаши', en: 'Gurubashi Trolls' }, description: { ru: 'Убей 10 троллей-охотников за головами.', en: 'Kill 10 troll headhunters.' }, type: 'kill', objective: { target: 'tribal', count: 10 }, reward: { xp: 2400, gold: 240, itemId: 'steel-greatsword' } },
  { id: 'z-lochmod', giver: 'lochmod-scout', giverName: { ru: 'Дозорный Лохмода', en: 'Lochmod Scout' }, giverSide: 'tidal', giverPos: { x: -2450, z: -400 }, minLevel: 10, maxLevel: 20, title: { ru: 'Кобольды у плотины', en: 'Dam Kobolds' }, description: { ru: 'Убей 8 пауков-кобольдов.', en: 'Kill 8 kobold spiders.' }, type: 'kill', objective: { target: 'spider', count: 8 }, reward: { xp: 850, gold: 75, itemId: 'leather-cap' } },
  { id: 'z-mireland', giver: 'mireland-warden', giverName: { ru: 'Страж Болотины', en: 'Mireland Warden' }, giverSide: 'tidal', giverPos: { x: -2450, z: 400 }, minLevel: 20, maxLevel: 30, title: { ru: 'Орки Сумеречного Молота', en: 'Twilight Hammer Orcs' }, description: { ru: 'Останови 6 орков-сектантов.', en: 'Stop 6 cultist orcs.' }, type: 'kill', objective: { target: 'orc', count: 6 }, reward: { xp: 1200, gold: 110, itemId: 'mail-leggings' } },
  { id: 'z-searing-gorge', giver: 'thorium-engineer', giverName: { ru: 'Инженер Ториевого Братства', en: 'Thorium Engineer' }, giverSide: null, giverPos: { x: -1400, z: 1400 }, minLevel: 45, maxLevel: 50, title: { ru: 'Огненные Элементали', en: 'Fire Elementals' }, description: { ru: 'Уничтожь 5 големов лавы.', en: 'Destroy 5 lava golems.' }, type: 'kill', objective: { target: 'golem', count: 5 }, reward: { xp: 4500, gold: 450, itemId: 'arcane-staff' } },
  { id: 'z-burnsteppe', giver: 'flamegard-commander', giverName: { ru: 'Командир Огнестража', en: 'Flamegard Commander' }, giverSide: null, giverPos: { x: -800, z: 1400 }, minLevel: 50, maxLevel: 55, title: { ru: 'Чёрные Драконыши', en: 'Black Dragon Whelps' }, description: { ru: 'Убей 8 чёрных драконышей.', en: 'Kill 8 black whelps.' }, type: 'kill', objective: { target: 'dragonkin', count: 8 }, reward: { xp: 5500, gold: 520, itemId: 'stormblade' } },
  { id: 'z-cinderlands', giver: 'cinder-watcher', giverName: { ru: 'Наблюдатель Пустоши', en: 'Cinder Watcher' }, giverSide: null, giverPos: { x: -200, z: 1400 }, minLevel: 45, maxLevel: 55, title: { ru: 'Демоны у Тёмного Портала', en: 'Demons at Dark Portal' }, description: { ru: 'Изгнани 6 демонов.', en: 'Banish 6 demons.' }, type: 'kill', objective: { target: 'demon', count: 6 }, reward: { xp: 4800, gold: 480, itemId: 'mail-hauberk' } },
  { id: 'z-sorrow-marsh', giver: 'sorrow-shaman', giverName: { ru: 'Шаман Скорби', en: 'Sorrow Shaman' }, giverSide: null, giverPos: { x: -1400, z: 900 }, minLevel: 35, maxLevel: 45, title: { ru: 'Мерлокские Гнёзда', en: 'Merloc Nests' }, description: { ru: 'Уничтожь 7 мерлоков.', en: 'Destroy 7 merlocs.' }, type: 'kill', objective: { target: 'spider', count: 7 }, reward: { xp: 2800, gold: 280, itemId: 'priest-robe' } },
  { id: 'z-hilsbrook', giver: 'hilsbrook-captain', giverName: { ru: 'Капитан Хилсбрука', en: 'Hilsbrook Captain' }, giverSide: null, giverPos: { x: -1950, z: 1000 }, minLevel: 20, maxLevel: 30, title: { ru: 'Битва за Тарренову Мельницу', en: 'Battle for Tarren Mill' }, description: { ru: 'Убей 8 врагов противоположной фракции.', en: 'Kill 8 opposing faction.' }, type: 'kill', objective: { target: 'bandit', count: 8 }, reward: { xp: 1500, gold: 150, realmPoints: 50 } },
  { id: 'z-arathih', giver: 'arathih-strategist', giverName: { ru: 'Стратег Аратии', en: 'Arathi Strategist' }, giverSide: null, giverPos: { x: -2450, z: 1400 }, minLevel: 30, maxLevel: 40, title: { ru: 'Захват Котловины', en: 'Basin Capture' }, description: { ru: 'Захвати точку контроля.', en: 'Capture a control point.' }, type: 'visit', objective: { pos: { x: -2450, z: 1400, radius: 100 } }, reward: { xp: 2200, gold: 220, realmPoints: 100 } },
  { id: 'z-tirisfal', giver: 'tirisfal-deathguard', giverName: { ru: 'Гвардеец Смерти Тирисфаля', en: 'Tirisfal Deathguard' }, giverSide: 'anchorless', giverPos: { x: -1300, z: 1300 }, minLevel: 1, maxLevel: 10, title: { ru: 'Чумные Псы', en: 'Plague Dogs' }, description: { ru: 'Убей 5 заражённых волков.', en: 'Kill 5 plagued wolves.' }, type: 'kill', objective: { target: 'wolf', count: 5 }, reward: { xp: 280, gold: 25, itemId: 'leather-cap' } },
  { id: 'z-silverpine', giver: 'silverpine-ranger', giverName: { ru: 'Лесничая Серебряного Бора', en: 'Silverpine Ranger' }, giverSide: 'anchorless', giverPos: { x: -1700, z: 1300 }, minLevel: 10, maxLevel: 20, title: { ru: 'Воргены Шадоуфэнга', en: 'Shadowfang Worgen' }, description: { ru: 'Уничтожь 7 воргенов.', en: 'Destroy 7 worgen.' }, type: 'kill', objective: { target: 'wolf', count: 7 }, reward: { xp: 800, gold: 80, itemId: 'oak-bow' } },
  { id: 'z-west-plagueland', giver: 'plague-paladin', giverName: { ru: 'Паладин Алого Возмездия', en: 'Crimson Paladin' }, giverSide: null, giverPos: { x: -2150, z: 1600 }, minLevel: 51, maxLevel: 58, title: { ru: 'Очищение Андорхала', en: 'Andorhal Purge' }, description: { ru: 'Уничтожь 8 чумных стражей.', en: 'Destroy 8 plague guardians.' }, type: 'kill', objective: { target: 'ghost_skull', count: 8 }, reward: { xp: 6500, gold: 650, itemId: 'mail-hauberk' } },
  { id: 'z-east-plagueland', giver: 'plague-archmage', giverName: { ru: 'Архимаг Святой Надежды', en: 'Light\'s Hope Archmage' }, giverSide: null, giverPos: { x: -1700, z: 1600 }, minLevel: 53, maxLevel: 60, title: { ru: 'Падший Стратхольм', en: 'Fallen Stratholme' }, description: { ru: 'Победи 10 нежить-стражей.', en: 'Defeat 10 undead guards.' }, type: 'kill', objective: { target: 'wraith', count: 10 }, reward: { xp: 8500, gold: 850, itemId: 'stormblade', realmPoints: 75 } },
  // East side
  { id: 'z-wilds', giver: 'wilds-overseer', giverName: { ru: 'Надзиратель Степей', en: 'Wilds Overseer' }, giverSide: 'anchorless', giverPos: { x: 1000, z: -300 }, minLevel: 10, maxLevel: 25, title: { ru: 'Кентавры Перекрёстка', en: 'Crossroads Centaurs' }, description: { ru: 'Убей 9 кентавров.', en: 'Kill 9 centaurs.' }, type: 'kill', objective: { target: 'tribal', count: 9 }, reward: { xp: 1100, gold: 100, itemId: 'iron-sword' } },
  { id: 'z-mulhorn', giver: 'mulhorn-elder', giverName: { ru: 'Старейшина Мульхорна', en: 'Mulhorn Elder' }, giverSide: 'anchorless', giverPos: { x: 900, z: 400 }, minLevel: 1, maxLevel: 10, title: { ru: 'Священное Кодо', en: 'Sacred Kodo' }, description: { ru: 'Защити стадо от 5 хищников.', en: 'Protect herd from 5 predators.' }, type: 'kill', objective: { target: 'wolf', count: 5 }, reward: { xp: 280, gold: 25, itemId: 'tattered-shirt' } },
  { id: 'z-stoneclaw', giver: 'stoneclaw-shaman', giverName: { ru: 'Шаман Когтистых Гор', en: 'Stoneclaw Shaman' }, giverSide: null, giverPos: { x: 1000, z: 400 }, minLevel: 15, maxLevel: 30, title: { ru: 'Харпии-налётчицы', en: 'Harpy Raiders' }, description: { ru: 'Победи 8 харпий.', en: 'Defeat 8 harpies.' }, type: 'kill', objective: { target: 'tribal', count: 8 }, reward: { xp: 1200, gold: 120, itemId: 'mail-leggings' } },
  { id: 'z-ashpine', giver: 'ashpine-druid', giverName: { ru: 'Друид Ясеня', en: 'Ash Druid' }, giverSide: 'tidal', giverPos: { x: 900, z: 950 }, minLevel: 18, maxLevel: 30, title: { ru: 'Война за Лесопилки', en: 'Mill War' }, description: { ru: 'Останови 7 лесорубов.', en: 'Stop 7 lumberjacks.' }, type: 'kill', objective: { target: 'orc', count: 7 }, reward: { xp: 1300, gold: 130, itemId: 'priest-robe', realmPoints: 50 } },
  { id: 'z-needlespires', giver: 'needle-engineer', giverName: { ru: 'Инженер Игл', en: 'Needle Engineer' }, giverSide: null, giverPos: { x: 1500, z: 900 }, minLevel: 25, maxLevel: 35, title: { ru: 'Шахта Освобождения', en: 'Liberation Mine' }, description: { ru: 'Очисти шахту от 6 гоблинов.', en: 'Clear mine of 6 goblins.' }, type: 'kill', objective: { target: 'goblin', count: 6 }, reward: { xp: 1700, gold: 170, itemId: 'arcane-staff' } },
  { id: 'z-dustmire', giver: 'dust-watcher', giverName: { ru: 'Следопыт Топи', en: 'Dust Tracker' }, giverSide: null, giverPos: { x: 1000, z: 1500 }, minLevel: 35, maxLevel: 45, title: { ru: 'Тёмная Чешуя', en: 'Dark Scale' }, description: { ru: 'Победи 6 драконышей Тёмной Чешуи.', en: 'Defeat 6 Dark Scale dragonkin.' }, type: 'kill', objective: { target: 'dragonkin', count: 6 }, reward: { xp: 2700, gold: 270, itemId: 'mail-hauberk' } },
  { id: 'z-wildshore', giver: 'wildshore-druid', giverName: { ru: 'Друид Дикого Берега', en: 'Wildshore Druid' }, giverSide: null, giverPos: { x: 1500, z: -1300 }, minLevel: 40, maxLevel: 50, title: { ru: 'Огры Гордунни', en: 'Gordunni Ogres' }, description: { ru: 'Убей 7 огров клана Гордунни.', en: 'Kill 7 Gordunni ogres.' }, type: 'kill', objective: { target: 'yeti', count: 7 }, reward: { xp: 3500, gold: 350, itemId: 'steel-greatsword' } },
  { id: 'z-sunsands', giver: 'sands-quartermaster', giverName: { ru: 'Караванщик Песков', en: 'Sands Caravan Master' }, giverSide: null, giverPos: { x: 1500, z: 1400 }, minLevel: 40, maxLevel: 50, title: { ru: 'Гадоксы пустыни', en: 'Desert Gadox' }, description: { ru: 'Убей 8 гадоксов-ящериц.', en: 'Kill 8 Gadox lizards.' }, type: 'kill', objective: { target: 'cactoro', count: 8 }, reward: { xp: 3700, gold: 370, itemId: 'hunter-cloak' } },
  { id: 'z-crater', giver: 'crater-druid', giverName: { ru: 'Друид Кратера', en: 'Crater Druid' }, giverSide: null, giverPos: { x: 200, z: 1400 }, minLevel: 48, maxLevel: 55, title: { ru: 'Динозавры Кратера', en: 'Crater Dinosaurs' }, description: { ru: 'Победи 5 хищных динозавров.', en: 'Defeat 5 predator dinos.' }, type: 'kill', objective: { target: 'dino', count: 5 }, reward: { xp: 5000, gold: 500, itemId: 'arcane-staff' } },
  { id: 'z-crystalwastes', giver: 'cenarion-watcher', giverName: { ru: 'Кенарийский Дозор', en: 'Cenarion Watcher' }, giverSide: null, giverPos: { x: 800, z: 1600 }, minLevel: 55, maxLevel: 60, title: { ru: 'Анубисат-стражи', en: 'Anubisath Guardians' }, description: { ru: 'Уничтожь 6 силитусских стражей.', en: 'Destroy 6 silithus guardians.' }, type: 'kill', objective: { target: 'dragonkin', count: 6 }, reward: { xp: 8000, gold: 800, itemId: 'crown-of-three-winds', realmPoints: 100 } },
  { id: 'z-moonglade', giver: 'moonglade-elder', giverName: { ru: 'Старейшина Лунной Поляны', en: 'Moonglade Elder' }, giverSide: null, giverPos: { x: 400, z: 1600 }, minLevel: 55, maxLevel: 60, title: { ru: 'Кенарийский Ритуал', en: 'Cenarion Rite' }, description: { ru: 'Помедитируй у каждой из четырёх сторон Лунной Поляны.', en: 'Meditate at each of the four cardinal points of Moonglade.' }, type: 'visit', objective: { pos: { x: 400, z: 1600, radius: 200 } }, reward: { xp: 7000, gold: 700, itemId: 'priest-robe' } },
  // R232q5t: 2-я квест-цепочка для каждой зоны (gather-вариант) — ещё 27 квестов
  { id: 'z2-westmarch', giver: 'westmarch-marshal', giverName: { ru: 'Маршал Западного Простора', en: 'Westmarch Marshal' }, giverSide: 'tidal', giverPos: { x: -1950, z: -300 }, minLevel: 12, maxLevel: 22, title: { ru: 'Шкуры волков для шорника', en: 'Wolf Pelts for the Saddler' }, description: { ru: 'Собери 6 волчьих шкур.', en: 'Collect 6 wolf pelts.' }, type: 'gather', objective: { target: 'wolf-pelt', count: 6 }, reward: { xp: 700, gold: 70, itemId: 'leather-jerkin' } },
  { id: 'z2-redrock', giver: 'redrock-overseer', giverName: { ru: 'Надзиратель Краснокаменья', en: 'Redrock Overseer' }, giverSide: 'tidal', giverPos: { x: -1300, z: -1250 }, minLevel: 17, maxLevel: 27, title: { ru: 'Драконьи кости', en: 'Dragon Bones' }, description: { ru: 'Принеси 5 драконьих чешуек с кладбища.', en: 'Bring 5 dragon scales.' }, type: 'gather', objective: { target: 'dragon-scale', count: 5 }, reward: { xp: 1000, gold: 95, itemId: 'mail-leggings' } },
  { id: 'z2-duskhollow', giver: 'duskhollow-priest', giverName: { ru: 'Жрица Сумрака', en: 'Dusk Priestess' }, giverSide: 'tidal', giverPos: { x: -2150, z: -1100 }, minLevel: 20, maxLevel: 32, title: { ru: 'Эктоплазма призраков', en: 'Wraith Ectoplasm' }, description: { ru: 'Собери 7 пузырьков эктоплазмы.', en: 'Collect 7 ectoplasm vials.' }, type: 'gather', objective: { target: 'ectoplasm', count: 7 }, reward: { xp: 1300, gold: 125, itemId: 'priest-robe' } },
  { id: 'z2-thorndeep', giver: 'thorndeep-explorer', giverName: { ru: 'Исследователь Тернии', en: 'Thorn Explorer' }, giverSide: null, giverPos: { x: -2450, z: 1100 }, minLevel: 32, maxLevel: 47, title: { ru: 'Шёлк джунглевых пауков', en: 'Jungle Spider Silk' }, description: { ru: 'Принеси 8 пучков паучьего шёлка.', en: 'Bring 8 bundles of spider silk.' }, type: 'gather', objective: { target: 'spider-silk', count: 8 }, reward: { xp: 2200, gold: 220, itemId: 'hunter-cloak' } },
  { id: 'z2-lochmod', giver: 'lochmod-scout', giverName: { ru: 'Дозорный Лохмода', en: 'Lochmod Scout' }, giverSide: 'tidal', giverPos: { x: -2450, z: -400 }, minLevel: 12, maxLevel: 22, title: { ru: 'Озёрная рыба для трактира', en: 'Lake Fish for the Inn' }, description: { ru: 'Налови 5 морских рыб.', en: 'Catch 5 sea fish.' }, type: 'gather', objective: { target: 'sea-fish', count: 5 }, reward: { xp: 750, gold: 70, itemId: 'rusted-sword' } },
  { id: 'z2-mireland', giver: 'mireland-warden', giverName: { ru: 'Страж Болотины', en: 'Mireland Warden' }, giverSide: 'tidal', giverPos: { x: -2450, z: 400 }, minLevel: 22, maxLevel: 32, title: { ru: 'Огненные кристаллы культистов', en: 'Cultist Fire Crystals' }, description: { ru: 'Собери 4 огненных кристалла.', en: 'Collect 4 fire crystals.' }, type: 'gather', objective: { target: 'fire-crystal', count: 4 }, reward: { xp: 1100, gold: 110, itemId: 'mail-hauberk' } },
  { id: 'z2-searing-gorge', giver: 'thorium-engineer', giverName: { ru: 'Инженер Ториевого Братства', en: 'Thorium Engineer' }, giverSide: null, giverPos: { x: -1400, z: 1400 }, minLevel: 47, maxLevel: 50, title: { ru: 'Каменные осколки големов', en: 'Golem Stone Shards' }, description: { ru: 'Принеси 5 каменных осколков.', en: 'Bring 5 stone shards.' }, type: 'gather', objective: { target: 'stone-shard', count: 5 }, reward: { xp: 4200, gold: 420, itemId: 'arcane-staff' } },
  { id: 'z2-burnsteppe', giver: 'flamegard-commander', giverName: { ru: 'Командир Огнестража', en: 'Flamegard Commander' }, giverSide: null, giverPos: { x: -800, z: 1400 }, minLevel: 52, maxLevel: 55, title: { ru: 'Драконьи кристаллы', en: 'Dragon Crystals' }, description: { ru: 'Собери 6 огненных кристаллов с драконышей.', en: 'Collect 6 fire crystals from whelps.' }, type: 'gather', objective: { target: 'fire-crystal', count: 6 }, reward: { xp: 5200, gold: 500, itemId: 'stormblade' } },
  { id: 'z2-cinderlands', giver: 'cinder-watcher', giverName: { ru: 'Наблюдатель Пустоши', en: 'Cinder Watcher' }, giverSide: null, giverPos: { x: -200, z: 1400 }, minLevel: 47, maxLevel: 55, title: { ru: 'Проклятые руны', en: 'Cursed Runes' }, description: { ru: 'Принеси 5 проклятых рун от демонов.', en: 'Bring 5 cursed runes.' }, type: 'gather', objective: { target: 'cursed-rune', count: 5 }, reward: { xp: 4600, gold: 460, itemId: 'priest-robe' } },
  { id: 'z2-sorrow-marsh', giver: 'sorrow-shaman', giverName: { ru: 'Шаман Скорби', en: 'Sorrow Shaman' }, giverSide: null, giverPos: { x: -1400, z: 900 }, minLevel: 37, maxLevel: 47, title: { ru: 'Ядовитые железы пауков', en: 'Spider Venom Glands' }, description: { ru: 'Принеси 6 ядовитых желёз.', en: 'Bring 6 venom glands.' }, type: 'gather', objective: { target: 'venom-gland', count: 6 }, reward: { xp: 2700, gold: 270, itemId: 'arcane-staff' } },
  { id: 'z2-hilsbrook', giver: 'hilsbrook-captain', giverName: { ru: 'Капитан Хилсбрука', en: 'Hilsbrook Captain' }, giverSide: null, giverPos: { x: -1950, z: 1000 }, minLevel: 22, maxLevel: 32, title: { ru: 'Знамёна разбойников', en: 'Bandit Banners' }, description: { ru: 'Собери 4 разбойничьих знамени.', en: 'Collect 4 bandit banners.' }, type: 'gather', objective: { target: 'leather-scrap', count: 4 }, reward: { xp: 1450, gold: 145, realmPoints: 40 } },
  { id: 'z2-arathih', giver: 'arathih-strategist', giverName: { ru: 'Стратег Аратии', en: 'Arathi Strategist' }, giverSide: null, giverPos: { x: -2450, z: 1400 }, minLevel: 32, maxLevel: 42, title: { ru: 'Шкуры воргов', en: 'Worg Pelts' }, description: { ru: 'Собери 6 шкур воргов с Нагорья.', en: 'Collect 6 worg pelts.' }, type: 'gather', objective: { target: 'wolf-pelt', count: 6 }, reward: { xp: 2100, gold: 210, itemId: 'hunter-cloak' } },
  { id: 'z2-tirisfal', giver: 'tirisfal-deathguard', giverName: { ru: 'Гвардеец Смерти Тирисфаля', en: 'Tirisfal Deathguard' }, giverSide: 'anchorless', giverPos: { x: -1300, z: 1300 }, minLevel: 3, maxLevel: 12, title: { ru: 'Заражённое мясо', en: 'Tainted Meat' }, description: { ru: 'Собери 5 кусков заражённого мяса волков.', en: 'Collect 5 tainted meat chunks.' }, type: 'gather', objective: { target: 'roast-meat', count: 5 }, reward: { xp: 320, gold: 28, itemId: 'tattered-shirt' } },
  { id: 'z2-silverpine', giver: 'silverpine-ranger', giverName: { ru: 'Лесничая Серебряного Бора', en: 'Silverpine Ranger' }, giverSide: 'anchorless', giverPos: { x: -1700, z: 1300 }, minLevel: 12, maxLevel: 22, title: { ru: 'Эктоплазма Шадоуфэнга', en: 'Shadowfang Ectoplasm' }, description: { ru: 'Принеси 6 пузырьков эктоплазмы.', en: 'Bring 6 ectoplasm vials.' }, type: 'gather', objective: { target: 'ectoplasm', count: 6 }, reward: { xp: 850, gold: 85, itemId: 'leather-jerkin' } },
  { id: 'z2-west-plagueland', giver: 'plague-paladin', giverName: { ru: 'Паладин Алого Возмездия', en: 'Crimson Paladin' }, giverSide: null, giverPos: { x: -2150, z: 1600 }, minLevel: 53, maxLevel: 58, title: { ru: 'Чумные грибы', en: 'Plague Mushrooms' }, description: { ru: 'Принеси 7 чумных грибов.', en: 'Bring 7 plague mushrooms.' }, type: 'gather', objective: { target: 'mushroom-stew', count: 7 }, reward: { xp: 6200, gold: 620, itemId: 'mail-hauberk' } },
  { id: 'z2-east-plagueland', giver: 'plague-archmage', giverName: { ru: 'Архимаг Святой Надежды', en: 'Light\'s Hope Archmage' }, giverSide: null, giverPos: { x: -1700, z: 1600 }, minLevel: 55, maxLevel: 60, title: { ru: 'Стратхольмские реликвии', en: 'Stratholme Relics' }, description: { ru: 'Найди 3 реликвии в руинах.', en: 'Find 3 relics in the ruins.' }, type: 'gather', objective: { target: 'cursed-rune', count: 3 }, reward: { xp: 8000, gold: 800, itemId: 'crown-of-three-winds', realmPoints: 100 } },
  { id: 'z2-wilds', giver: 'wilds-overseer', giverName: { ru: 'Надзиратель Степей', en: 'Wilds Overseer' }, giverSide: 'anchorless', giverPos: { x: 1000, z: -300 }, minLevel: 12, maxLevel: 27, title: { ru: 'Бычьи шкуры', en: 'Bull Hides' }, description: { ru: 'Собери 7 шкур быков.', en: 'Collect 7 bull hides.' }, type: 'gather', objective: { target: 'boar-hide', count: 7 }, reward: { xp: 1000, gold: 95, itemId: 'mail-leggings' } },
  { id: 'z2-mulhorn', giver: 'mulhorn-elder', giverName: { ru: 'Старейшина Мульхорна', en: 'Mulhorn Elder' }, giverSide: 'anchorless', giverPos: { x: 900, z: 400 }, minLevel: 3, maxLevel: 12, title: { ru: 'Кодо-молоко', en: 'Kodo Milk' }, description: { ru: 'Принеси 4 кувшина кодо-молока.', en: 'Bring 4 kodo milk pitchers.' }, type: 'gather', objective: { target: 'cheese-wedge', count: 4 }, reward: { xp: 320, gold: 28, itemId: 'tattered-shirt' } },
  { id: 'z2-stoneclaw', giver: 'stoneclaw-shaman', giverName: { ru: 'Шаман Когтистых Гор', en: 'Stoneclaw Shaman' }, giverSide: null, giverPos: { x: 1000, z: 400 }, minLevel: 17, maxLevel: 32, title: { ru: 'Перья харпий', en: 'Harpy Feathers' }, description: { ru: 'Собери 5 перьев харпий для ритуала.', en: 'Collect 5 harpy feathers.' }, type: 'gather', objective: { target: 'leather-scrap', count: 5 }, reward: { xp: 1100, gold: 110, itemId: 'mail-hauberk' } },
  { id: 'z2-ashpine', giver: 'ashpine-druid', giverName: { ru: 'Друид Ясеня', en: 'Ash Druid' }, giverSide: 'tidal', giverPos: { x: 900, z: 950 }, minLevel: 20, maxLevel: 32, title: { ru: 'Лунные ягоды', en: 'Moonberries' }, description: { ru: 'Собери 6 пучков лунных ягод.', en: 'Collect 6 moonberries.' }, type: 'gather', objective: { target: 'apple', count: 6 }, reward: { xp: 1200, gold: 120, itemId: 'priest-robe', realmPoints: 30 } },
  { id: 'z2-needlespires', giver: 'needle-engineer', giverName: { ru: 'Инженер Игл', en: 'Needle Engineer' }, giverSide: null, giverPos: { x: 1500, z: 900 }, minLevel: 27, maxLevel: 35, title: { ru: 'Грубые кинжалы гоблинов', en: 'Goblin Crude Daggers' }, description: { ru: 'Принеси 5 грубых кинжалов.', en: 'Bring 5 crude daggers.' }, type: 'gather', objective: { target: 'crude-dagger', count: 5 }, reward: { xp: 1600, gold: 160, itemId: 'arcane-staff' } },
  { id: 'z2-dustmire', giver: 'dust-watcher', giverName: { ru: 'Следопыт Топи', en: 'Dust Tracker' }, giverSide: null, giverPos: { x: 1000, z: 1500 }, minLevel: 37, maxLevel: 45, title: { ru: 'Чешуя Тёмной Чешуи', en: 'Dark Scale Scales' }, description: { ru: 'Собери 5 драконьих чешуек.', en: 'Collect 5 dragon scales.' }, type: 'gather', objective: { target: 'dragon-scale', count: 5 }, reward: { xp: 2500, gold: 250, itemId: 'mail-hauberk' } },
  { id: 'z2-wildshore', giver: 'wildshore-druid', giverName: { ru: 'Друид Дикого Берега', en: 'Wildshore Druid' }, giverSide: null, giverPos: { x: 1500, z: -1300 }, minLevel: 42, maxLevel: 50, title: { ru: 'Огрские черепа', en: 'Ogre Skulls' }, description: { ru: 'Принеси 4 ограмских черепа для ритуала.', en: 'Bring 4 ogre skulls.' }, type: 'gather', objective: { target: 'cracked-tooth', count: 4 }, reward: { xp: 3300, gold: 330, itemId: 'steel-greatsword' } },
  { id: 'z2-sunsands', giver: 'sands-quartermaster', giverName: { ru: 'Караванщик Песков', en: 'Sands Caravan Master' }, giverSide: null, giverPos: { x: 1500, z: 1400 }, minLevel: 42, maxLevel: 50, title: { ru: 'Кактусовая мука', en: 'Cactus Flour' }, description: { ru: 'Собери 6 пучков кактусовой муки.', en: 'Collect 6 cactus flour.' }, type: 'gather', objective: { target: 'mushroom-stew', count: 6 }, reward: { xp: 3500, gold: 350, itemId: 'hunter-cloak' } },
  { id: 'z2-crater', giver: 'crater-druid', giverName: { ru: 'Друид Кратера', en: 'Crater Druid' }, giverSide: null, giverPos: { x: 200, z: 1400 }, minLevel: 50, maxLevel: 55, title: { ru: 'Динозавровые яйца', en: 'Dinosaur Eggs' }, description: { ru: 'Найди 3 неоплодотворённых яйца.', en: 'Find 3 unfertilised eggs.' }, type: 'gather', objective: { target: 'spider-silk', count: 3 }, reward: { xp: 4800, gold: 480, itemId: 'arcane-staff' } },
  { id: 'z2-crystalwastes', giver: 'cenarion-watcher', giverName: { ru: 'Кенарийский Дозор', en: 'Cenarion Watcher' }, giverSide: null, giverPos: { x: 800, z: 1600 }, minLevel: 55, maxLevel: 60, title: { ru: 'Кристаллы Ан\'Кираж', en: 'Ahn\'Qiraj Crystals' }, description: { ru: 'Собери 5 фиолетовых кристаллов.', en: 'Collect 5 violet crystals.' }, type: 'gather', objective: { target: 'fire-crystal', count: 5 }, reward: { xp: 7500, gold: 750, itemId: 'crown-of-three-winds', realmPoints: 100 } },
  { id: 'z2-moonglade', giver: 'moonglade-elder', giverName: { ru: 'Старейшина Лунной Поляны', en: 'Moonglade Elder' }, giverSide: null, giverPos: { x: 400, z: 1600 }, minLevel: 55, maxLevel: 60, title: { ru: 'Сонная роса', en: 'Slumber Dew' }, description: { ru: 'Собери 4 капли росы у каждого тотема Поляны.', en: 'Collect 4 dewdrops at each Glade totem.' }, type: 'gather', objective: { target: 'apple', count: 4 }, reward: { xp: 6800, gold: 680, itemId: 'priest-robe' } },
];

export function questsForGiver(giverId: string): Quest[] {
  return QUESTS.filter((q) => q.giver === giverId);
}

export function questsAvailableTo(level: number, side: Side, completedIds: string[] = []): Quest[] {
  return QUESTS.filter((q) => {
    if (level < q.minLevel) return false;
    if (level > q.maxLevel + 5) return false;
    if (q.giverSide && q.giverSide !== side) return false;
    if (q.prereqs && q.prereqs.some((p) => !completedIds.includes(p))) return false;
    if (completedIds.includes(q.id)) return false;
    return true;
  });
}

export function questById(id: string): Quest | undefined {
  return QUESTS.find((q) => q.id === id);
}

// R153: интеграция 72 vanilla starter quests из vanilla-starter-quests.ts
// в основной QUESTS массив. Поскольку оригинальные quests на сервере
// не имеют giver-position координат, ставим их у starter-zone центра.
import { VANILLA_STARTER_QUESTS } from './vanilla-starter-quests.js';

const VANILLA_ZONE_GIVER_POS: Record<string, { x: number; z: number; side: Side }> = {
  greenwald:  { x: -650,  z: 0,    side: 'tidal' },
  dunhammer:  { x: -1100, z: -900, side: 'tidal' },
  worldroot:  { x: 900,   z: 1300, side: 'tidal' },        // night-elf side
  redrock:    { x: 650,   z: 0,    side: 'anchorless' },
  mulhorn:    { x: 400,   z: 900,  side: 'anchorless' },
  hollow:     { x: -1500, z: 1500, side: 'anchorless' },
};

const VANILLA_GIVER_NAMES_RU: Record<string, string> = {
  greenwald:  'Командор Тирен Стальгорн',
  dunhammer:  'Сенатор Барин Каменотёс',
  worldroot:  'Нириэль Шёлковый Ветер',
  redrock:    'Сардар Шарпкрест',
  mulhorn:    'Громовая Грива',
  hollow:     'Аптекарь Берринг',
};

for (const vq of VANILLA_STARTER_QUESTS) {
  const zoneInfo = VANILLA_ZONE_GIVER_POS[vq.zone];
  if (!zoneInfo) continue;
  // Не дублировать если quest с таким id уже есть.
  if (QUESTS.find((q) => q.id === `vanilla-${vq.id}`)) continue;
  const type: QuestType = vq.objective.kind === 'collect' ? 'gather'
    : vq.objective.kind === 'kill' ? 'kill' : 'visit';
  QUESTS.push({
    id: `vanilla-${vq.id}`,
    giver: `vanilla-giver-${vq.zone}`,
    giverName: { ru: VANILLA_GIVER_NAMES_RU[vq.zone] ?? 'Неизвестный', en: '???' },
    giverSide: zoneInfo.side,
    giverPos: { x: zoneInfo.x, z: zoneInfo.z },
    minLevel: vq.minLevel,
    maxLevel: vq.level + 5,
    title: vq.title,
    description: {
      ru: `Стартовое задание. Цель: ${vq.objective.target} × ${vq.objective.count}.`,
      en: `Starter quest. Target: ${vq.objective.target} × ${vq.objective.count}.`,
    },
    type,
    objective: { target: vq.objective.target, count: vq.objective.count },
    reward: { xp: 200 + vq.level * 80, gold: 5 + vq.level * 2 },
  });
}
